import React, { useEffect, useState } from 'react';
import EmptyState from '../../../components/page/EmptyState';
import PageHero from '../../../components/page/PageHero';
import SectionHeader from '../../../components/page/SectionHeader';
import { useAuth } from '../../../shared/AuthContext';
import rolePermissions from '../../../shared/rolePermissions';

const Component = () => {
  const { user } = useAuth();
  const canManageUsers = rolePermissions.hasPermission(user, 'users.manage');
  const roleChoices = rolePermissions.ROLE_ORDER.map((role) => ({
    value: role,
    label: rolePermissions.ROLE_DEFINITIONS[role].label,
  }));
  const [users, setUsers] = useState([]);
  const [serverUsers, setServerUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [inviteResult, setInviteResult] = useState('');
  const [inviteForm, setInviteForm] = useState({
    email: '',
    fullName: '',
    role: 'member',
  });

  async function loadUsers() {
    setLoading(true);
    setError('');
    try {

      const Factory = require( '../../../records/auth' );

      const response = await Factory.listUsers();
      const rows = response.data || [];
      setUsers(rows);
      setServerUsers(rows);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Unable to load users.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (canManageUsers) {
      loadUsers();
    }
  }, [canManageUsers]);

  if (!canManageUsers) {
    return (
      <div className="stack users-page">
        <EmptyState
          description="A users.manage permission is required for user management."
          title="Users"
        />
      </div>
    );
  }

  async function handleInvite(event) {
    event.preventDefault();
    setError('');
    setSuccess('');
    setInviteResult('');
    try {

      const Factory = require( '../../../records/auth' );

      const response = await Factory.createInvite({
        email: inviteForm.email.trim(),
        fullName: inviteForm.fullName.trim(),
        role: inviteForm.role,
      });
      setSuccess('Invite sent successfully.');
      setInviteResult(response.inviteUrl || '');
      setInviteForm({ email: '', fullName: '', role: 'member' });
      await loadUsers();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Unable to send invite.');
    }
  }

  async function handleSaveUser(row) {
    const serverRow = serverUsers.find((item) => item.id === row.id);
    if (
      row.status === 'disabled'
      && serverRow?.status !== 'disabled'
      && !window.confirm(`Disable account for ${row.email}? They will not be able to log in until reactivated.`)
    ) {
      return;
    }

    setSavingId(row.id);
    setError('');
    setSuccess('');
    try {

      const Factory = require( '../../../records/auth' );

      await Factory.updateUser(row.id, {
        role: row.role,
        status: row.status,
        fullName: row.fullName || '',
      });
      setSuccess(`Updated ${row.email}.`);
      await loadUsers();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Unable to update user.');
    } finally {
      setSavingId('');
    }
  }

  async function handleResendInvite(row) {
    setSavingId(row.id);
    setError('');
    setSuccess('');
    try {

      const Factory = require( '../../../records/auth' );

      const response = await Factory.createInvite({
        email: row.email,
        fullName: row.fullName || '',
        role: row.role,
      });
      setSuccess(`Invite re-sent to ${row.email}.`);
      setInviteResult(response.inviteUrl || '');
      await loadUsers();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Unable to resend invite.');
    } finally {
      setSavingId('');
    }
  }

  function updateLocalUser(id, field, value) {
    setUsers((prev) => prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
  }

  return (
    <div className="stack users-page">
      <PageHero
        subtitle="Invite teammates and manage workspace access."
        title={`Users${user?.roleLabel ? ` · ${user.roleLabel}` : ''}`}
      />

      {error ? <p className="error-text">{error}</p> : null}
      {success ? <p className="success-text">{success}</p> : null}
      {inviteResult ? <p className="dev-hint">Invite link: {inviteResult}</p> : null}

      <section className="card stack-sm">
        <SectionHeader title="Invite user" />
        <form className="inline-form-card users-invite-grid" onSubmit={handleInvite}>
          <input
            placeholder="Email"
            type="email"
            value={inviteForm.email}
            onChange={(event) => setInviteForm((prev) => ({ ...prev, email: event.target.value }))}
            required
          />
          <input
            placeholder="Full name"
            value={inviteForm.fullName}
            onChange={(event) => setInviteForm((prev) => ({ ...prev, fullName: event.target.value }))}
          />
          <select
            value={inviteForm.role}
            onChange={(event) => setInviteForm((prev) => ({ ...prev, role: event.target.value }))}
          >
            {roleChoices.map((roleChoice) => (
              <option key={roleChoice.value} value={roleChoice.value}>
                {roleChoice.label}
              </option>
            ))}
          </select>
          <button className="button-primary" type="submit">Send invite</button>
        </form>
      </section>

      <section className="card table-card">
        <SectionHeader title="Workspace users" />
        {loading ? <p>Loading users...</p> : null}
        {!loading ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Full Name</th>
                <th>Role</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((row) => (
                <tr key={row.id}>
                  <td>{row.email}</td>
                  <td>
                    <input
                      value={row.fullName || ''}
                      onChange={(event) => updateLocalUser(row.id, 'fullName', event.target.value)}
                    />
                  </td>
                  <td>
                    <select
                      value={row.role}
                      onChange={(event) => updateLocalUser(row.id, 'role', event.target.value)}
                    >
                      {roleChoices.map((roleChoice) => (
                        <option key={roleChoice.value} value={roleChoice.value}>
                          {roleChoice.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      value={row.status}
                      onChange={(event) => updateLocalUser(row.id, 'status', event.target.value)}
                    >
                      <option value="invited">Invited</option>
                      <option value="active">Active</option>
                      <option value="disabled">Disabled</option>
                    </select>
                  </td>
                  <td>{row.lastLoginAt ? new Date(row.lastLoginAt).toLocaleString() : '-'}</td>
                  <td className="table-actions">
                    <button
                      className="button-secondary"
                      disabled={savingId === row.id}
                      onClick={() => handleSaveUser(row)}
                      type="button"
                    >
                      Save
                    </button>
                    {row.status === 'invited' ? (
                      <button
                        className="button-secondary"
                        disabled={savingId === row.id}
                        onClick={() => handleResendInvite(row)}
                        type="button"
                      >
                        Resend invite
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </section>
    </div>
  );
};

export default Component;